/**
 * Cart Drawer - Bundle Product Management
 * Handles bundle product display, discounts, and cart calculations in the cart drawer
 * 
 * Required data attributes on the data container:
 * - data-section-id: section ID
 * - data-cart-items: JSON of cart items
 * - data-has-discount-codes: boolean
 */

(function() {
  // Get data container
  const dataContainer = document.querySelector('[data-cart-drawer-bundle-data]');
  
  // Get section ID and initial cart data
  const sectionId = dataContainer?.dataset.sectionId || '';
  const hasDiscountCodes = dataContainer?.dataset.hasDiscountCodes === 'true';
  
  // Parse initial cart items
  let initialCartItems = [];
  try {
    initialCartItems = JSON.parse(dataContainer?.dataset.cartItems || '[]');
  } catch (e) {
    initialCartItems = [];
  }
  
  // Execution lock to prevent multiple simultaneous initializations
  let isInitializing = false;
  let isInitialized = false;
  
  // Cache DOM queries
  const domCache = {
    cartDrawer: null,
    cartItemsContainer: null,
    getCartDrawer() {
      return this.cartDrawer || (this.cartDrawer = document.querySelector('cart-drawer'));
    },
    getCartItemsContainer() {
      return this.cartItemsContainer || (this.cartItemsContainer = document.querySelector(`#CartDrawerBody-${sectionId}`));
    },
    clear() {
      this.cartDrawer = null;
      this.cartItemsContainer = null;
    }
  };
  
  // Function to get bundle data from cart item properties
  function getBundleDataFromItem(item) {
    if (!item.properties || !item.properties._is_bundle) {
      return null;
    }

    let productsData = null;
    if (item.properties._bundle_products_data) {
      try {
        productsData = JSON.parse(item.properties._bundle_products_data);
      } catch (e) {
        // Silent fail
      }
    }

    return {
      variantId: item.properties._variant_id,
      discountValue: item.properties._discount_value,
      bundleQuantity: parseInt(item.properties._bundle_quantity) || 1,
      userQuantity: parseInt(item.properties._user_quantity) || 1,
      isBundle: item.properties._is_bundle === 'true',
      productsData: productsData,
      productVariantId: item.properties._product_id
    };
  }

  // Function to group products by bundle ID
  function groupProductsByBundleVariant() {
    const cartItems = window.currentCartItems || initialCartItems;
    const bundleGroups = {};

    cartItems.forEach((item, index) => {
      if (item.properties && item.properties._is_bundle && item.properties._bundle_id) {
        const bundleId = item.properties._bundle_id;
        if (!bundleGroups[bundleId]) {
          bundleGroups[bundleId] = [];
        }
        bundleGroups[bundleId].push({
          index: index,
          item: item,
          bundleData: getBundleDataFromItem(item)
        });
      }
    });

    return bundleGroups;
  }

  // Function to validate if products are actually bundled
  function validateBundleGroup(group) {
    if (group.length < 2) {
      return false; // Need at least 2 products for a bundle
    }

    // Get the first item's data as reference
    const firstItem = group[0];
    const referenceProductsData = firstItem.bundleData.productsData;
    const referenceVariantId = firstItem.item.properties._variant_id;

    if (!referenceProductsData || !Array.isArray(referenceProductsData)) {
      return false;
    }

    // Check if all items in the group have the same bundle variant ID (bundle product ID)
    const hasSameVariantId = group.every(item => {
      return item.item.properties._variant_id === referenceVariantId;
    });

    if (!hasSameVariantId) {
      return false;
    }

    // Get the expected component variant IDs from the bundle products data
    const expectedVariantIds = referenceProductsData.map((item) => item.id).sort((a, b) => a - b);

    // Get the actual component variant IDs in the current group
    const actualVariantIds = group.map((item) => item.bundleData.productVariantId).sort((a, b) => a - b);

    // Check if we have ALL the required components (not just some of them)
    const hasAllComponents = expectedVariantIds.every(expectedId =>
      actualVariantIds.includes(expectedId)
    );

    const isValidBundle = hasAllComponents && actualVariantIds.length === expectedVariantIds.length;

    return isValidBundle;
  }

  // Function to apply bundle discounts to component products
  function applyBundleDiscounts() {
    const bundleGroups = groupProductsByBundleVariant();
    const cartItems = window.currentCartItems || initialCartItems;

    // Process bundle groups (don't reset anything initially to preserve automatic discounts)
    // We'll handle resets on a case-by-case basis within each bundle group
    Object.keys(bundleGroups).forEach(bundleId => {
      const group = bundleGroups[bundleId];
      const isValidBundle = validateBundleGroup(group);

      if (isValidBundle) {
        // Valid bundle group - show both original and discounted prices with bundle indicator
        group.forEach(({ index, item, bundleData }) => {
          const cartItemElement = document.querySelector(`#CartDrawer-Item-${index + 1}`);
          // Reset to original price first, then apply bundle discount
          resetToOriginalPrice(cartItemElement, item);
          applyBundleDiscountToCartItem(index, item, bundleData);
          // Hide discount tags for valid bundle items
          if (cartItemElement) {
            toggleDiscountTags(cartItemElement, true);
          }
        });
      } else {
        // Invalid bundle group - show as regular products
        group.forEach(({ index, item }) => {
          const cartItemElement = document.querySelector(`#CartDrawer-Item-${index + 1}`);
          if (cartItemElement) {
            // Check if this item has an automatic discount
            if (item.original_price !== item.final_price) {
              // Has automatic discount - apply automatic discount display
              applyAutomaticDiscountDisplay(cartItemElement, item);
            } else {
              // No automatic discount - reset to original price
              resetToOriginalPrice(cartItemElement, item);
            }
            removeBundleIndicator(cartItemElement);
            // Show discount tags for invalid bundle items (if they have discounts)
            toggleDiscountTags(cartItemElement, false);
          }
        });
      }
    });

    // Ensure non-bundle products show correct prices (apply automatic discounts)
    cartItems.forEach((item, index) => {
      if (!item.properties || !item.properties._is_bundle) {
        const cartItemElement = document.querySelector(`#CartDrawer-Item-${index + 1}`);
        if (cartItemElement) {
          // Check if this product has an automatic discount
          if (item.original_price !== item.final_price) {
            // Has automatic discount - apply automatic discount display
            applyAutomaticDiscountDisplay(cartItemElement, item);
            // Ensure discount tags are visible for regular products with discounts
            toggleDiscountTags(cartItemElement, false);
          } else {
            // No discount - reset to ensure clean display
            resetToOriginalPrice(cartItemElement, item);
            removeBundleIndicator(cartItemElement);
          }
        }
      }
    });
  }

  // Function to reset price display to original
  function resetToOriginalPrice(cartItemElement, item) {
    if (!cartItemElement) return;
    
    // Get bundle data to determine if we need to show true original price
    const bundleData = getBundleDataFromItem(item);
    let displayPrice = item.original_price;
    let displayLinePrice = item.original_line_price;

    // If this is a bundle item, get the true original price
    if (bundleData) {
      const trueOriginalPrice = getOriginalProductPrice(item, bundleData);
      displayPrice = trueOriginalPrice;
      displayLinePrice = trueOriginalPrice * item.quantity;
    }

    // Reset unit prices (only .price elements, not line totals)
    const priceElements = cartItemElement.querySelectorAll('.price');

    priceElements.forEach(priceElement => {
      // Remove any existing discount styling
      priceElement.classList.remove('price--on-sale');

      // Clear existing price content
      priceElement.innerHTML = '';

      // Format original unit price
      const formattedPrice = new Intl.NumberFormat('no-NO', {
        style: 'currency',
        currency: 'NOK'
      }).format(displayPrice / 100);

      // Display original unit price
      const priceSpan = document.createElement('span');
      priceSpan.className = 'font-body-bolder';
      priceSpan.textContent = formattedPrice;
      priceElement.appendChild(priceSpan);
    });
  }

  // Function to apply bundle discount to a specific cart item (shows both original and discounted prices)
  function applyBundleDiscountToCartItem(itemIndex, item, bundleData) {
    const cartItemElement = document.querySelector(`#CartDrawer-Item-${itemIndex + 1}`);
    if (!cartItemElement) {
      return;
    }

    const discountPercent = parseFloat(bundleData.discountValue) || 0;
    if (discountPercent <= 0) {
      return;
    }

    // Get the true original product price (not the already discounted price)
    const trueOriginalPrice = getOriginalProductPrice(item, bundleData);
    const discountAmount = (trueOriginalPrice * discountPercent) / 100;
    const discountedPrice = trueOriginalPrice - discountAmount;

    // Update price display (shows both original and discounted prices)
    updateBundlePriceDisplay(cartItemElement, trueOriginalPrice, discountedPrice, discountPercent);
  }

  // Function to remove bundle indicator
  function removeBundleIndicator(cartItemElement) {
    if (!cartItemElement) return;
    const bundleIndicator = cartItemElement.querySelector('.bundle-indicator');
    if (bundleIndicator) {
      bundleIndicator.remove();
    }
  }

  // Function to toggle discount tag visibility based on bundle validity
  function toggleDiscountTags(cartItemElement, isValidBundle) {
    if (!cartItemElement) return;
    const discountsList = cartItemElement.querySelector('.cart-item__discounts');
    if (discountsList) {
      if (isValidBundle) {
        // Valid bundle - hide discount tags
        discountsList.style.display = 'none';
      } else {
        // Invalid bundle - show discount tags
        discountsList.style.display = '';
      }
    }
  }

  // Function to apply automatic discount display (for products with Shopify automatic discounts)
  function applyAutomaticDiscountDisplay(cartItemElement, item) {
    if (!cartItemElement) return;
    const priceElements = cartItemElement.querySelectorAll('.price');

    priceElements.forEach((priceElement) => {
      // Clear existing content
      priceElement.innerHTML = '';

      // Format prices
      const formattedOriginalPrice = new Intl.NumberFormat('no-NO', {
        style: 'currency',
        currency: 'NOK'
      }).format(item.original_price / 100);

      const formattedFinalPrice = new Intl.NumberFormat('no-NO', {
        style: 'currency',
        currency: 'NOK'
      }).format(item.final_price / 100);

      // Create final price (current price) - this is the main price
      const regularPriceSpan = document.createElement('span');
      regularPriceSpan.className = 'price__regular font-body-bolder';
      regularPriceSpan.textContent = formattedFinalPrice;
      priceElement.appendChild(regularPriceSpan);

      // Create original price (crossed out)
      const salePriceSpan = document.createElement('s');
      salePriceSpan.className = 'price__sale font-body text-subtext';
      salePriceSpan.textContent = formattedOriginalPrice;
      priceElement.appendChild(salePriceSpan);

      // Add sale styling to show both prices
      priceElement.classList.add('price--on-sale');
    });
  }

  // Function to update bundle price display (shows both original and discounted prices)
  function updateBundlePriceDisplay(cartItemElement, originalPrice, discountedPrice, discountPercent) {
    if (!cartItemElement) return;
    // Find price elements in both desktop and mobile views
    const priceElements = cartItemElement.querySelectorAll('.price');

    priceElements.forEach((priceElement, index) => {
      // Clear existing content
      priceElement.innerHTML = '';

      // Format prices
      const formattedOriginalPrice = new Intl.NumberFormat('no-NO', {
        style: 'currency',
        currency: 'NOK'
      }).format(originalPrice / 100);

      const formattedDiscountedPrice = new Intl.NumberFormat('no-NO', {
        style: 'currency',
        currency: 'NOK'
      }).format(discountedPrice / 100);

      // Create discounted price (current price) - this is the main price
      const regularPriceSpan = document.createElement('span');
      regularPriceSpan.className = 'price__regular font-body-bolder';
      regularPriceSpan.textContent = formattedDiscountedPrice;
      priceElement.appendChild(regularPriceSpan);

      // Create original price (crossed out) - this shows the original price
      const salePriceSpan = document.createElement('s');
      salePriceSpan.className = 'price__sale font-body text-subtext';
      salePriceSpan.textContent = formattedOriginalPrice;
      priceElement.appendChild(salePriceSpan);

      // Add sale styling to show both prices
      priceElement.classList.add('price--on-sale');
    });
  }

  // Function to update quantity controls based on bundle validity
  function updateQuantityControls() {
    const cartItems = window.currentCartItems || initialCartItems;
    const bundleGroups = groupProductsByBundleVariant();

    cartItems.forEach((item, index) => {
      const cartItemElement = document.querySelector(`#CartDrawer-Item-${index + 1}`);
      if (!cartItemElement) return;

      const quantityInput = cartItemElement.querySelector('input[name="updates[]"]');
      const minusButton = cartItemElement.querySelector('button[name="minus"]');
      const plusButton = cartItemElement.querySelector('button[name="plus"]');
      const quantityWrapper = cartItemElement.querySelector('.quantity');

      if (!quantityInput || !quantityWrapper) return;

      // Check if this is a bundle product
      if (item.properties && item.properties._is_bundle && item.properties._bundle_id) {
        const bundleId = item.properties._bundle_id;
        const bundleGroup = bundleGroups[bundleId];
        const isValidBundle = bundleGroup && validateBundleGroup(bundleGroup);

        if (isValidBundle) {
          // Valid bundle - disable controls
          quantityInput.disabled = true;
          quantityInput.readOnly = true;
          if (minusButton) minusButton.disabled = true;
          if (plusButton) plusButton.disabled = true;
          quantityWrapper.classList.add('quantity--disabled');
          if (minusButton) minusButton.classList.add('disabled');
          if (plusButton) plusButton.classList.add('disabled');
          quantityInput.classList.add('disabled');
        } else {
          // Invalid bundle - enable controls
          quantityInput.disabled = false;
          quantityInput.readOnly = false;
          if (minusButton) minusButton.disabled = false;
          if (plusButton) plusButton.disabled = false;
          quantityWrapper.classList.remove('quantity--disabled');
          if (minusButton) minusButton.classList.remove('disabled');
          if (plusButton) plusButton.classList.remove('disabled');
          quantityInput.classList.remove('disabled');
        }
      } else {
        // Regular product - ensure controls are enabled
        quantityInput.disabled = false;
        quantityInput.readOnly = false;
        if (minusButton) minusButton.disabled = false;
        if (plusButton) plusButton.disabled = false;
        quantityWrapper.classList.remove('quantity--disabled');
        if (minusButton) minusButton.classList.remove('disabled');
        if (plusButton) plusButton.classList.remove('disabled');
        quantityInput.classList.remove('disabled');
      }
    });
  }

  // Function to update cart drawer subtotal based on actual prices
  function updateCartDrawerSubtotal() {
    // Always get fresh cart data
    const cartItems = window.currentCartItems || initialCartItems;

    // If discount codes are applied, completely skip JavaScript override
    // Let Shopify handle the calculation with discount codes
    if (hasDiscountCodes) {
      return;
    }

    let calculatedTotal = 0;

    // Get bundle groups to check which items should have discounts
    const bundleGroups = groupProductsByBundleVariant();

    cartItems.forEach((item, index) => {
      // Check if this is a bundle product and if it's part of a valid bundle group
      if (item.properties && item.properties._is_bundle && item.properties._bundle_id) {
        const bundleId = item.properties._bundle_id;
        const bundleGroup = bundleGroups[bundleId];
        const isValidBundle = bundleGroup && validateBundleGroup(bundleGroup);

        if (isValidBundle && item.properties._discount_value) {
          // This is a valid bundle product with discount - get true original price and calculate discount
          const discountPercent = parseFloat(item.properties._discount_value) || 0;

          // Get bundle data to find true original price
          const bundleData = getBundleDataFromItem(item);
          let trueOriginalPrice = item.original_price; // Use original_price from cart item

          if (bundleData && bundleData.productsData && Array.isArray(bundleData.productsData)) {
            const productData = bundleData.productsData.find(p => p.id == item.variant_id);
            if (productData && productData.price) {
              trueOriginalPrice = productData.price;
            }
          }

          // Calculate true original line price and then apply discount
          const trueOriginalLinePrice = trueOriginalPrice * item.quantity;
          const discountAmount = (trueOriginalLinePrice * discountPercent) / 100;
          const discountedLinePrice = trueOriginalLinePrice - discountAmount;
          calculatedTotal += discountedLinePrice;
        } else {
          // This is an invalid bundle - check if it has automatic discount
          if (item.original_price !== item.final_price) {
            // Invalid bundle item with automatic discount - use final_line_price
            calculatedTotal += item.final_line_price;
          } else {
            // Invalid bundle item without automatic discount - use true original price
            const bundleData = getBundleDataFromItem(item);
            let trueOriginalPrice = item.original_price; // Use original_price from cart item

            if (bundleData && bundleData.productsData && Array.isArray(bundleData.productsData)) {
              const productData = bundleData.productsData.find(p => p.id == item.variant_id);
              if (productData && productData.price) {
                trueOriginalPrice = productData.price;
              }
            }

            const trueOriginalLinePrice = trueOriginalPrice * item.quantity;
            calculatedTotal += trueOriginalLinePrice;
          }
        }
      } else {
        // This is a regular product - check if it has automatic discount
        if (item.original_price !== item.final_price) {
          // Has automatic discount - use final_line_price
          calculatedTotal += item.final_line_price;
        } else {
          // No discount - use original_line_price
          calculatedTotal += item.original_line_price;
        }
      }
    });

    // Update the subtotal display
    const subtotalElement = document.getElementById('cart-drawer-subtotal-value');

    if (subtotalElement) {
      const formattedTotal = new Intl.NumberFormat('no-NO', {
        style: 'currency',
        currency: 'NOK'
      }).format(calculatedTotal / 100);

      subtotalElement.textContent = formattedTotal;
    }
  }

  // Function to update bundle product display
  function updateBundleProductDisplay() {
    applyBundleDiscounts();
    updateQuantityControls();
    updateCartDrawerSubtotal();
  }

  // Function to reinitialize bundle display after cart changes (with execution lock and debounce)
  let reinitializeTimeout = null;
  async function reinitializeBundleDisplay() {
    // Prevent multiple simultaneous executions
    if (isInitializing) {
      return;
    }
    
    // Clear existing timeout
    if (reinitializeTimeout) {
      clearTimeout(reinitializeTimeout);
    }
    
    // Debounce the reinitialization
    reinitializeTimeout = setTimeout(async () => {
      isInitializing = true;
      
      try {
        // Get fresh cart data (uses cache from cart.js if available)
        const currentCartData = await getCurrentCartData();
        if (currentCartData && currentCartData.items) {
          // Update the cart items in the global scope for the bundle functions
          window.currentCartItems = currentCartData.items;
        }

        // Small delay to ensure DOM is updated
        requestAnimationFrame(() => {
          updateBundleProductDisplay();
          isInitialized = true;
        });
      } catch (error) {
        if (Shopify?.designMode) {
          console.error('Error reinitializing bundle display:', error);
        }
      } finally {
        isInitializing = false;
      }
    }, 150);
  }

  // Function to get current cart data - uses cached version from cart.js if available
  async function getCurrentCartData() {
    // Use cached fetchCartData from cart.js if available, otherwise fallback
    if (typeof window.fetchCartData === 'function') {
      try {
        return await window.fetchCartData();
      } catch (error) {
        if (Shopify?.designMode) {
          console.error('Error fetching cart data:', error);
        }
        return null;
      }
    }
    
    // Fallback if cart.js cache not available
    try {
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      return cartData;
    } catch (error) {
      if (Shopify?.designMode) {
        console.error('Error fetching cart data:', error);
      }
      return null;
    }
  }

  // Function to force refresh cart drawer content
  async function forceRefreshCartDrawer() {
    try {
      // Get the cart drawer section ID
      const cartDrawer = document.querySelector('cart-drawer');
      const cartDrawerSectionId = cartDrawer ? cartDrawer.getAttribute('data-section-id') : null;

      if (cartDrawerSectionId) {
        // Fetch fresh cart drawer content
        const response = await fetch(`/?section_id=${cartDrawerSectionId}`);
        const responseText = await response.text();

        const parser = new DOMParser();
        const parsedHTML = parser.parseFromString(responseText, 'text/html');

        // Update cart drawer body
        const cartDrawerBody = document.querySelector(`#CartDrawerBody-${cartDrawerSectionId}`);
        const newCartDrawerBody = parsedHTML.querySelector(`#CartDrawerBody-${cartDrawerSectionId}`);

        if (cartDrawerBody && newCartDrawerBody) {
          cartDrawerBody.innerHTML = newCartDrawerBody.innerHTML;
        }

        // Update cart drawer footer
        const cartDrawerFooter = document.querySelector(`#CartDrawerFooter-${cartDrawerSectionId}`);
        const newCartDrawerFooter = parsedHTML.querySelector(`#CartDrawerFooter-${cartDrawerSectionId}`);

        if (cartDrawerFooter && newCartDrawerFooter) {
          cartDrawerFooter.innerHTML = newCartDrawerFooter.innerHTML;
        }

        // Update cart drawer empty state
        const cartDrawerEmpty = document.querySelector(`#CartDrawerEmpty-${cartDrawerSectionId}`);
        const newCartDrawerEmpty = parsedHTML.querySelector(`#CartDrawerEmpty-${cartDrawerSectionId}`);

        if (cartDrawerEmpty && newCartDrawerEmpty) {
          cartDrawerEmpty.innerHTML = newCartDrawerEmpty.innerHTML;
        }
      }

      // Get fresh cart data and update bundle display
      const currentCartData = await getCurrentCartData();
      if (currentCartData && currentCartData.items) {
        window.currentCartItems = currentCartData.items;

        // Show/hide cart drawer sections based on cart state
        const cartDrawerBody = document.querySelector(`#CartDrawerBody-${sectionId}`);
        const cartDrawerFooter = document.querySelector(`#CartDrawerFooter-${sectionId}`);
        const cartDrawerEmpty = document.querySelector(`#CartDrawerEmpty-${sectionId}`);

        if (currentCartData.item_count > 0) {
          if (cartDrawerBody) cartDrawerBody.classList.remove('hidden');
          if (cartDrawerFooter) cartDrawerFooter.classList.remove('hidden');
          if (cartDrawerEmpty) cartDrawerEmpty.classList.add('hidden');
        } else {
          if (cartDrawerBody) cartDrawerBody.classList.add('hidden');
          if (cartDrawerFooter) cartDrawerFooter.classList.add('hidden');
          if (cartDrawerEmpty) cartDrawerEmpty.classList.remove('hidden');
        }
      }

      // Update bundle display with fresh data
      setTimeout(() => {
        updateBundleProductDisplay();
      }, 100);

    } catch (error) {
      // Silent fail
    }
  }

  // Function to get original product price from product data
  function getOriginalProductPrice(item, bundleData) {
    // If we have the original product data in bundleData.productsData, use that
    if (bundleData.productsData && Array.isArray(bundleData.productsData)) {
      const productData = bundleData.productsData.find(p => p.id == item.variant_id);
      if (productData && productData.price) {
        return productData.price;
      }
    }

    // Fallback: calculate original price by reversing the discount
    const discountPercent = parseFloat(bundleData.discountValue) || 0;
    if (discountPercent > 0) {
      // If current price is already discounted, calculate original price
      const currentPrice = item.original_price;
      const originalPrice = Math.round(currentPrice / (1 - discountPercent / 100));
      return originalPrice;
    }

    // If no discount, return current price
    return item.original_price;
  }

  // Initialize bundle logic ONLY when cart drawer opens (not on page load)
  function initializeBundleCart() {
    // Prevent multiple initializations
    if (isInitializing || isInitialized) {
      return;
    }
    
    isInitializing = true;
    
    getCurrentCartData().then(currentCartData => {
      if (currentCartData && currentCartData.items) {
        window.currentCartItems = currentCartData.items;
      }
      updateBundleProductDisplay();
      isInitialized = true;
      isInitializing = false;
    }).catch(error => {
      if (Shopify?.designMode) {
        console.error('Error initializing bundle cart:', error);
      }
      isInitializing = false;
    });
  }

  // Listen for cart drawer opening to initialize/refresh bundle logic
  const cartDrawer = domCache.getCartDrawer();
  if (cartDrawer) {
    // Merged MutationObserver with debounce for drawer opening
    let drawerOpenTimeout = null;
    const mergedObserver = new MutationObserver((mutations) => {
      // Clear existing timeout
      if (drawerOpenTimeout) {
        clearTimeout(drawerOpenTimeout);
      }
      
      // Debounce observer callbacks
      drawerOpenTimeout = setTimeout(() => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'hidden') {
            const drawer = mutation.target;
            if (!drawer.hasAttribute('hidden')) {
              // Drawer opened - initialize bundle logic
              initializeBundleCart();
            }
          }
        });
      }, 150);
    });

    mergedObserver.observe(cartDrawer, { 
      attributes: true,
      attributeFilter: ['hidden']
    });

    // Also listen for the drawer's show method being called
    const originalShow = cartDrawer.show;
    if (originalShow) {
      cartDrawer.show = function(...args) {
        const result = originalShow.apply(this, args);
        // Initialize bundle logic when drawer is shown
        initializeBundleCart();
        return result;
      };
    }
  }

  // Listen for cart updates and reinitialize bundle display
  document.addEventListener('cart:updated', reinitializeBundleDisplay);
  document.addEventListener('cart:refresh', reinitializeBundleDisplay);
  document.addEventListener('cart:change', reinitializeBundleDisplay);

  // Also listen for product added events to refresh bundle display
  document.addEventListener('product-ajax:added', function(event) {
    setTimeout(() => {
      reinitializeBundleDisplay();
    }, 100);
  });

  // Listen for quantity changes
  document.addEventListener('change', function(event) {
    if (event.target.matches('input[name="updates[]"]')) {
      // Check if this is a bundle product
      const cartItem = event.target.closest('[id^="CartDrawer-Item-"]');
      if (cartItem) {
        const cartItemIndex = cartItem.id.replace('CartDrawer-Item-', '');
        const cartItems = window.currentCartItems || initialCartItems;
        const item = cartItems[parseInt(cartItemIndex) - 1];

        if (item && item.properties && item.properties._is_bundle && item.properties._bundle_id) {
          // Check if this bundle group is still valid
          const bundleGroups = groupProductsByBundleVariant();
          const bundleId = item.properties._bundle_id;
          const bundleGroup = bundleGroups[bundleId];
          const isValidBundle = bundleGroup && validateBundleGroup(bundleGroup);

          if (isValidBundle) {
            // Reset to original quantity
            event.target.value = item.quantity;
            event.preventDefault();
            return false;
          }
        }
      }

      reinitializeBundleDisplay();
    }
  });

  // Listen for item removals and quantity button clicks
  document.addEventListener('click', function(event) {
    if (event.target.closest('[is="cart-remove-item"]') || event.target.closest('[is="gift-wrap-remove-item"]')) {
      // Immediate update first
      setTimeout(() => {
        reinitializeBundleDisplay();
      }, 100);
      // Then full reinitialize
      setTimeout(() => {
        reinitializeBundleDisplay();
      }, 500); // Longer delay for removal
    }

    // Prevent quantity button clicks for bundle products
    if (event.target.matches('.quantity__button')) {
      const cartItem = event.target.closest('[id^="CartDrawer-Item-"]');
      if (cartItem) {
        const cartItemIndex = cartItem.id.replace('CartDrawer-Item-', '');
        const cartItems = window.currentCartItems || initialCartItems;
        const item = cartItems[parseInt(cartItemIndex) - 1];

        if (item && item.properties && item.properties._is_bundle && item.properties._bundle_id) {
          // Check if this bundle group is still valid
          const bundleGroups = groupProductsByBundleVariant();
          const bundleId = item.properties._bundle_id;
          const bundleGroup = bundleGroups[bundleId];
          const isValidBundle = bundleGroup && validateBundleGroup(bundleGroup);

          if (isValidBundle) {
            event.preventDefault();
            event.stopPropagation();
            return false;
          }
        }
      }
    }
  });

  // Make functions globally available
  window.getBundleDataFromItem = getBundleDataFromItem;
  window.updateBundleProductDisplay = updateBundleProductDisplay;
  window.groupProductsByBundleVariant = groupProductsByBundleVariant;
  window.validateBundleGroup = validateBundleGroup;
  window.applyBundleDiscounts = applyBundleDiscounts;
  window.updateQuantityControls = updateQuantityControls;
  window.updateCartDrawerSubtotal = updateCartDrawerSubtotal;
  window.reinitializeBundleDisplay = reinitializeBundleDisplay;
  window.forceRefreshCartDrawer = forceRefreshCartDrawer;
  window.toggleDiscountTags = toggleDiscountTags;
  window.applyAutomaticDiscountDisplay = applyAutomaticDiscountDisplay;

  // Manual trigger function
  window.forceBundleUpdate = function() {
    reinitializeBundleDisplay();
  };

  // Add manual trigger for subtotal update
  window.forceSubtotalUpdate = function() {
    updateCartDrawerSubtotal();
  };
})();
